/**
 * Mapper pur : xlsx rows Rentio → { biens, locataires }.
 *
 * Version browser-safe du mapper utilisé par scripts/import-rentio-mapper.ts.
 * Réutilisé par la page admin /parametres/import-xlsx qui parse le xlsx
 * côté browser via exceljs et envoie le résultat à l'Edge Function
 * import-xlsx-merge.
 *
 * NOTE : si modifié, garder en sync avec scripts/import-rentio-mapper.ts
 * (ou dédupliquer plus tard via un alias Vite).
 */

function getRegionFromPostalCode(code: string): string | null {
  const num = parseInt(code, 10);
  if (isNaN(num)) return null;
  if (num >= 1000 && num <= 1299) return "bruxelles";
  if (num >= 1500 && num <= 3999) return "flandre";
  if (num >= 8000 && num <= 9999) return "flandre";
  if (num >= 1300 && num <= 1499) return "wallonie";
  if (num >= 4000 && num <= 7999) return "wallonie";
  return null;
}

export type RentioRow = Record<string, unknown>;

export type BienInsertRow = {
  id: string;
  reference: string;
  referenceExterne: string;
  referenceInterne: string;
  projet: string;
  typeBien: string;
  adresse: string;
  propertyBox: string;
  codePostal: string;
  ville: string;
  region: string | null;
  classePeb: string;
  consoKwh: string;
  pebFinLe: string;
  surfaceHabitable: string;
  etage: string;
  etagesTotal: string;
  nbChambres: string;
  nbSdb: string;
  meuble: boolean | null;
  surfaceTerrasse: string;
  disponibilite: string;
  finDeContrat: string;
  companyContract: boolean;
};

export type LocataireInsertRow = {
  bien_id: string;
  type: "actuel";
  nom: string;
  email: string;
  telephone: string;
  dateDebut: string;
  dateFin: string;
  loyerInitial: string;
  loyerActuel: string;
  chargesInitiales: string;
  chargesActuelles: string;
  indexBaseYear: string;
};

export type MapperResult = {
  biens: BienInsertRow[];
  locataires: LocataireInsertRow[];
  stats: {
    totalRows: number;
    apartmentRows: number;
    uniqueProperties: number;
    rotationsDeduped: number;
    locatairesCreated: number;
  };
  warnings: string[];
};

function asStr(v: unknown): string {
  if (v === null || v === undefined) return "";
  return String(v).trim();
}

function asIsoDate(v: unknown): string {
  if (v === null || v === undefined || v === "") return "";
  if (v instanceof Date) {
    if (isNaN(v.getTime())) return "";
    return v.toISOString().slice(0, 10);
  }
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return "";
}

function titleCase(s: string): string {
  return s
    .toLowerCase()
    .split(/(\s+|-)/)
    .map((w) => (w.length > 0 && /\w/.test(w[0]) ? w[0].toUpperCase() + w.slice(1) : w))
    .join("");
}

function parseEpcValue(v: unknown): string {
  if (v === null || v === undefined || v === "") return "";
  const s = String(v);
  const nums = s.match(/\d+(?:\.\d+)?/g);
  if (!nums || nums.length === 0) return "";
  const max = Math.max(...nums.map(Number).filter((n) => !isNaN(n)));
  return isNaN(max) ? "" : String(max);
}

function normalizePeb(v: unknown): string {
  const s = asStr(v);
  if (!s) return "";
  const upper = s.toUpperCase();
  if (["NA", "UNKNOWN", "N/A"].includes(upper)) return "";
  if (/^(A\+|A|B|C|D|E|F|G)$/.test(upper)) return upper;
  return "";
}

function asBool(v: unknown): boolean | null {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  const s = String(v).trim().toLowerCase();
  if (["1", "true", "yes", "oui"].includes(s)) return true;
  if (["0", "false", "no", "non"].includes(s)) return false;
  return null;
}

function isoToDate(iso: string): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

export function rowsToBiens(rows: RentioRow[], today: Date = new Date()): MapperResult {
  const warnings: string[] = [];
  const totalRows = rows.length;

  const apartments = rows.filter((r) => {
    const t = asStr(r.propertyType).toLowerCase();
    return t === "apartment";
  });
  const apartmentRows = apartments.length;

  const groups = new Map<string, RentioRow[]>();
  for (const r of apartments) {
    const pid = asStr(r.propertyId);
    if (!pid) {
      warnings.push(`row sans propertyId ignorée`);
      continue;
    }
    if (!groups.has(pid)) groups.set(pid, []);
    groups.get(pid)!.push(r);
  }

  const picked: RentioRow[] = [];
  let rotationsDeduped = 0;
  for (const [, contracts] of groups) {
    if (contracts.length > 1) rotationsDeduped += contracts.length - 1;
    const sorted = [...contracts].sort((a, b) => {
      const da = isoToDate(asIsoDate(a.contractStartDate));
      const db = isoToDate(asIsoDate(b.contractStartDate));
      const ta = da ? da.getTime() : 0;
      const tb = db ? db.getTime() : 0;
      if (tb !== ta) return tb - ta;
      const ea = isoToDate(asIsoDate(a.contractStopDate));
      const eb = isoToDate(asIsoDate(b.contractStopDate));
      return (eb ? eb.getTime() : 0) - (ea ? ea.getTime() : 0);
    });
    picked.push(sorted[0]);
  }

  const biens: BienInsertRow[] = [];
  const locataires: LocataireInsertRow[] = [];

  for (const r of picked) {
    const id = asStr(r.propertyId);
    if (!id) {
      warnings.push(`bien sans propertyId rejeté`);
      continue;
    }

    const codePostal = asStr(r.propertyZip);
    const villeRaw = asStr(r.propertyCity);
    const ville = villeRaw ? titleCase(villeRaw) : "";
    const region = codePostal ? getRegionFromPostalCode(codePostal) : null;
    const rue = asStr(r.propertyStreet);
    const num = asStr(r.propertyNumber);
    const adresse = [rue, num].filter(Boolean).join(" ").trim();

    const finDeContrat = asIsoDate(r.contractStopDate);
    const forRent = asStr(r.forRentStatus).toLowerCase();

    let disponibilite: string;
    if (forRent === "forrent") {
      const finDate = isoToDate(finDeContrat);
      if (finDate && finDate < today) {
        disponibilite = "DISPONIBLE";
      } else {
        disponibilite = "EN PRÉPARATION";
      }
    } else {
      disponibilite = "LOUÉ";
    }

    const bien: BienInsertRow = {
      id,
      reference: id,
      referenceExterne: id,
      referenceInterne: asStr(r.propertyName),
      projet: asStr(r.groupName),
      typeBien: "APARTMENT",
      adresse,
      propertyBox: asStr(r.propertyBox),
      codePostal,
      ville,
      region,
      classePeb: normalizePeb(r.PEB),
      consoKwh: parseEpcValue(r.propertyEpcValue),
      pebFinLe: asIsoDate(r.propertyEpcExpirationDate),
      surfaceHabitable: asStr(r.propertyBuildingSurfaceArea),
      etage: asStr(r.propertyBuildingFloor),
      etagesTotal: asStr(r.propertyBuildingFloorCount),
      nbChambres: asStr(r.propertyCountBedroom),
      nbSdb: asStr(r.propertyCountBathroom),
      meuble: asBool(r.propertyIsFurnished),
      surfaceTerrasse: asStr(r.propertyBuildingTerraceSurfaceArea),
      disponibilite,
      finDeContrat,
      companyContract: asStr(r.VatNumber).length > 0,
    };
    biens.push(bien);

    const lastName = asStr(r.tenantContactLastName);
    const firstName = asStr(r.tenantContactFirstName);
    if (lastName || firstName) {
      locataires.push({
        bien_id: id,
        type: "actuel",
        nom: [firstName, lastName].filter(Boolean).join(" ").trim(),
        email: asStr(r.tenantContactEmail),
        telephone: asStr(r.tenantContactPhone),
        dateDebut: asIsoDate(r.contractStartDate),
        dateFin: asIsoDate(r.contractStopDate),
        loyerInitial: asStr(r.contractStartPrice),
        loyerActuel: asStr(r.contractCurrentPrice),
        chargesInitiales: asStr(r.contractStartCostsCommon),
        chargesActuelles: asStr(r.contractCurrentCostsCommon),
        indexBaseYear: asStr(r.contractIndexBaseYear),
      });
    }
  }

  return {
    biens,
    locataires,
    stats: {
      totalRows,
      apartmentRows,
      uniqueProperties: biens.length,
      rotationsDeduped,
      locatairesCreated: locataires.length,
    },
    warnings,
  };
}
